import { useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import { useTheme } from '@/lib/styling';
import type { Theme } from '@/theme';
import { Box, PressableBox, Text } from '@/components/ui/primitives';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { betterAuthClient } from '@/lib/betterAuthClient';
import { clientLogger } from '@/lib/datadog';
import { getAuthHref } from '@/lib/routes';
import { isIOS, isWeb } from '@/lib/platform';

const RESET_REDIRECT_URL =
  isWeb && typeof window !== 'undefined' &&
  (window.location.hostname === 'pickempickem.com' || window.location.hostname === 'www.pickempickem.com')
    ? `${window.location.origin}/reset-password`
    : 'https://pckm.now/reset-password';
const GENERIC_SUCCESS_MESSAGE = 'If an account exists for that email, we sent a reset link.';

export default function ForgotPasswordScreen() {
  const router = useRouter();
  const theme = useTheme<Theme>();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setErrorMessage('Enter a valid email address.');
      setSuccessMessage(null);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await betterAuthClient.requestPasswordReset({
        email: normalizedEmail,
        redirectTo: RESET_REDIRECT_URL,
      });

      if (result.error) {
        clientLogger.warn('Password reset request returned an error', { error: result.error });
      }

      setSuccessMessage(GENERIC_SUCCESS_MESSAGE);
    } catch (error) {
      clientLogger.error('Password reset request failed', { error });
      setErrorMessage('Could not send a reset link. Check your connection and try again.');
      setSuccessMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box style={styles.screen}>
      <Box style={styles.gridLayer} />
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <KeyboardAvoidingView behavior={isIOS ? 'padding' : undefined} style={styles.panel}>
          <Box style={styles.header}>
            <Text variant="caption" style={styles.kicker}>ACCOUNT ACCESS / RECOVERY</Text>
            <Box style={styles.titleWrap}>
              <Box style={styles.acidSlab} />
              <Text variant="h2" style={styles.title}>Forgot password?</Text>
            </Box>
            <Text variant="body" color="pressMuted">
              Enter your email and we&apos;ll send a secure link if an account exists.
            </Text>
          </Box>

          <Box gap="m">
            <Input
              value={email}
              onChangeText={setEmail}
              placeholder="Email"
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              editable={!isSubmitting}
              testID="forgot-password-email-input"
            />
          </Box>

          {errorMessage ? <Text color="destructive">{errorMessage}</Text> : null}
          {successMessage ? <Text color="success">{successMessage}</Text> : null}

          <Button
            onPress={() => void handleSubmit()}
            disabled={isSubmitting}
            size="lg"
            testID="forgot-password-submit-button"
          >
            {isSubmitting ? (
              <ActivityIndicator color={theme.colors.press} />
            ) : (
              <Text variant="button" color="primaryForeground">Send reset link</Text>
            )}
          </Button>

          <PressableBox onPress={() => router.replace(getAuthHref())} alignSelf="center" padding="s">
            <Text variant="button" color="press">Back to sign in</Text>
          </PressableBox>
        </KeyboardAvoidingView>
      </ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create((theme, runtime) => {
  const isWide = runtime.screen.width >= theme.breakpoints.sm;

  return {
    acidSlab: {
      backgroundColor: theme.colors.acid,
      bottom: -4,
      height: 6,
      left: 0,
      position: 'absolute',
      right: 8,
    },
    content: {
      alignItems: 'center',
      flexGrow: 1,
      justifyContent: 'center',
      paddingBottom: 48,
      paddingHorizontal: isWide ? 32 : 16,
      paddingTop: isWide ? 76 : 36,
    },
    gridLayer: {
      borderColor: theme.colors.press,
      borderWidth: 1,
      bottom: 0,
      left: 0,
      opacity: 0.1,
      position: 'absolute',
      right: 0,
      top: 0,
      transform: [{ rotate: '1deg' }],
    },
    header: { gap: 12 },
    kicker: { color: theme.colors.pressMuted },
    panel: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 5,
      elevation: 8,
      gap: theme.spacing.l,
      maxWidth: 560,
      padding: isWide ? 28 : 18,
      shadowColor: theme.colors.press,
      shadowOffset: { width: 14, height: 14 },
      shadowOpacity: 1,
      shadowRadius: 0,
      width: '100%',
    },
    screen: { backgroundColor: theme.colors.background, flex: 1 },
    title: { color: theme.colors.press },
    titleWrap: { alignSelf: 'flex-start', position: 'relative' },
  };
});
